const protocol =
  window.location.protocol === "http:" ? "http:" : window.location.protocol;

export const COMPARE_API_BASE = `${protocol}//${window.location.hostname}/wdqmsapi/v1/compare`;

export const DIFF_COLORS = {
  up_2plus:      "#1a7a1a",
  up_1:          "#52b852",
  no_change:     "#4a90d9",
  down_1:        "#f5a05a",
  down_2plus:    "#c0392b",
  no_data_p2:    "#000000",
  no_data_p1:    "#7b2d8b",
  no_data_both:  "#555555",
};

export const AVAIL_COLORS = {
  more_than_100: "#ff33cc",
  normal:        "#009900",
  small_issues:  "#ff9900",
  big_issues:    "#ff3300",
  not_received:  "#000000",
  oscar_issue:   "#969696",
};

export const TIMELINESS_COLORS = {
  lt_15min:  "#3b4cc0",
  lt_30min:  "#6687ed",
  lt_120min: "#f59d7e",
  gt_120min: "#b40426",
};

export const QUALITY_COLORS = [
  "#006d2c", "#41ab5d", "#a1d99b", "#fed976", "#fd8d3c", "#e31a1c",
];

export const VARIABLES = [
  { value: "110", label: "Surface pressure (hPa)" },
  { value: "39",  label: "2m temperature (K)" },
  { value: "41",  label: "10m zonal wind (m/s)" },
  { value: "42",  label: "10m meridional wind (m/s)" },
  { value: "58",  label: "2m relative humidity (%)" },
];

export const METRICS = [
  { value: "availability", label: "Availability" },
  { value: "timeliness",   label: "Timeliness" },
  { value: "quality",      label: "Quality" },
];

export const PERIOD_TYPES = [
  { value: "daily",   label: "Daily" },
  { value: "monthly", label: "Monthly" },
];

export const ALL_CENTERS = ["DWD", "ECMWF", "JMA", "NCEP"];

export const DIFF_LEGEND_ITEMS = [
  {
    color:   DIFF_COLORS.up_2plus,
    label:   "Strong improvement (≥ 2 steps)",
    tooltip: "Station moved up 2 or more WDQMS category steps between P1 and P2. E.g. from 'Not received' to 'Normal'.",
  },
  {
    color:   DIFF_COLORS.up_1,
    label:   "Mild improvement (1 step)",
    tooltip: "Station moved up exactly 1 WDQMS category step between P1 and P2.",
  },
  {
    color:   DIFF_COLORS.no_change,
    label:   "No change",
    tooltip: "Station remained in the same WDQMS category in both periods. Includes stations reporting above 100% in one period and normal in the other (both rank 0).",
  },
  {
    color:   DIFF_COLORS.down_1,
    label:   "Mild degradation (1 step)",
    tooltip: "Station moved down exactly 1 WDQMS category step between P1 and P2.",
  },
  {
    color:   DIFF_COLORS.down_2plus,
    label:   "Strong degradation (≥ 2 steps)",
    tooltip: "Station moved down 2 or more WDQMS category steps between P1 and P2. E.g. from 'Normal' to 'Not received'.",
  },
  {
    color:   DIFF_COLORS.no_data_p2,
    label:   "Stopped reporting in P2",
    tooltip: "Observations found in Period 1 but none in Period 2. The station may have gone offline or been decommissioned.",
  },
  {
    color:   DIFF_COLORS.no_data_p1,
    label:   "Not present in P1 (new in P2)",
    tooltip: "No observations found for this station in Period 1. It first appears in Period 2 — possibly a newly registered or recently activated station.",
  },
];

export const getAvailabilityCategories = () => [
  { rank: 0, label: "Normal (≥ 80%)",             color: AVAIL_COLORS.normal },
  { rank: 1, label: "Availability issues (≥ 30%)", color: AVAIL_COLORS.small_issues },
  { rank: 2, label: "Availability issues (< 30%)", color: AVAIL_COLORS.big_issues },
  { rank: 3, label: "Not received in period",       color: AVAIL_COLORS.not_received },
];

export const getTimelinessCategories = () => [
  { rank: 0, label: "< 15 minutes",      color: TIMELINESS_COLORS.lt_15min },
  { rank: 1, label: "15 – 30 minutes",   color: TIMELINESS_COLORS.lt_30min },
  { rank: 2, label: "30 – 120 minutes",  color: TIMELINESS_COLORS.lt_120min },
  { rank: 3, label: "> 120 minutes",     color: TIMELINESS_COLORS.gt_120min },
];

export const getQualityCategories = (varId) => {
  const id = String(varId);
  const labels = {
    "110": ["≤ 0.5 hPa", "≤ 1 hPa", "≤ 5 hPa", "≤ 10 hPa", "> 10 hPa"],
    "39":  ["≤ 0.5 K",   "≤ 1 K",   "≤ 2 K",   "≤ 5 K",    "≤ 10 K",   "> 10 K"],
    "41":  ["≤ 0.5 m/s", "≤ 2 m/s", "≤ 3 m/s", "≤ 5 m/s",  "≤ 15 m/s", "> 15 m/s"],
    "42":  ["≤ 0.5 m/s", "≤ 2 m/s", "≤ 3 m/s", "≤ 5 m/s",  "≤ 15 m/s", "> 15 m/s"],
    "58":  ["≤ 0.02%",   "≤ 0.05%", "≤ 0.10%", "≤ 0.15%",  "≤ 0.30%",  "> 0.30%"],
    "1":   ["≤ 30 m",    "≤ 40 m",  "≤ 100 m", "> 100 m"],
  };
  const ranks = labels[id] || labels["110"];
  return ranks.map((label, i) => ({
    rank: i,
    label,
    color: QUALITY_COLORS[Math.min(i, QUALITY_COLORS.length - 1)],
  }));
};

export const getCategoryList = (metric, varId) => {
  if (metric === "availability") return getAvailabilityCategories();
  if (metric === "timeliness")   return getTimelinessCategories();
  return getQualityCategories(varId);
};
